<script setup lang="ts">
import { useGalleryCategories } from "@/composables/gallery/useFilterCategories";
import { MaterialQueryValues } from "@/composables/useMaterialQueriesNew";
import { FilterableRoute } from "@/types/filter.types";

const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();

const subNavItems = [
  {
    label: t("materials.subNav.fobizz"),
    to: { name: "gallery-index" },
  },
  {
    label: t("materials.subNav.toTeach"),
    to: { name: "gallery-to-teach" },
  },
  {
    label: t("materials.subNav.community"),
    to: { name: "gallery-community" },
  },
  {
    label: t("materials.subNav.folders"),
    to: { name: "folders-index" },
  },
];

const international = computed(() => {
  return (route.query.international as string) || userStore.user?.international
    ? userStore.user?.international.toString()
    : "false";
});

const defaultParams = {
  query: "",
  order: "created_at",
  international: international.value,
  models: [],
  schoolTypes: [],
  subjectTypes: [],
  gradeLevels: [],
  ids: "",
  userIds: [],
  page: 1,
  limit: 10,
};
const defaultParamsToIgnore = ["page", "limit", "international", "order"];

const { params, updateFilter, resetFilters, hasActiveFilters, search } =
  useFilter<MaterialQueryValues>({
    defaultParams,
  });

const currentFilterableRoute = computed(() => {
  return route.name as FilterableRoute;
});

const { categories } = useGalleryCategories(
  international,
  currentFilterableRoute
);

const handleSearchInput = (value: string) => {
  updateFilter("order", "relevant");
  search(value);
};
const handleSubmit = (query: string) => {
  search(query);
};

const handleFilterChange = (data: {
  label: string;
  value: MaterialQueryValues[keyof MaterialQueryValues];
}) => {
  updateFilter("order", "relevant");
  updateFilter(data.label as keyof MaterialQueryValues, data.value);
};

const handleResetFilters = () => {
  resetFilters();
};
const placeholderText = computed(() => {
  if (route.name === "gallery-index") {
    return t("materials.labels.galleryPlaceholder");
  } else if (route.name === "gallery-community") {
    return t("materials.labels.galleryCommunityPlaceholder");
  } else if (route.name === "gallery-to-teach") {
    return t("materials.labels.galleryToTeachPlaceholder");
  } else {
    return t("materials.labels.galleryFallbackPlaceholder");
  }
});
</script>

<template>
  <TheLayout :with-main-margin="false" enable-sub-nav>
    <MainFobizzNavbar>
      <template #tabs>
        <SubNav :items="subNavItems" />
      </template>
    </MainFobizzNavbar>
    <div class="materials sub-nav-margin">
      <FilterContent
        :categories="categories"
        :query="params.query"
        :query-placeholder="placeholderText || t('materials.searchPlaceholder')"
        :has-query="hasActiveFilters"
        :current-params="params as MaterialQueryValues"
        :default-params="defaultParamsToIgnore"
        @query="handleSearchInput"
        @submit="handleSubmit"
        @update:model-value="handleFilterChange"
        @reset="handleResetFilters"
      />
      <router-view :params="params" />
    </div>
  </TheLayout>
</template>
